import { MultiSelect, Select, SimpleGrid, Text, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  getPatientTimezone,
  normalizeUsStateCode,
  setPatientTimezone,
  US_STATE_SELECT_OPTIONS,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import type { JSX } from 'react';
import { useCallback, useState } from 'react';
import { GENDER_IDENTITY_OPTIONS, PRONOUNS_OPTIONS, SEX_AT_BIRTH_OPTIONS } from '../../lib/constants';
import { SettingsSection } from './SettingsSection';
import {
  formatDate,
  friendlyErrorMessage,
  getBirthSex,
  getGenderIdentity,
  getPronouns,
  getTelecomValue,
  setBirthSex,
  setGenderIdentity,
  setPronouns,
  setTelecomValue,
  stripSurroundingQuotes,
} from './settings.utils';
import { US_TIMEZONES } from './timezones';

interface PersonalInfoFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  birthDate: string;
  birthSex: string;
  gender: string;
  pronouns: string[];
  streetAddress: string;
  unit: string;
  city: string;
  state: string;
  zipcode: string;
  timezone: string;
}

function resolveGenderValue(rawCode: string): string {
  // If the stored code matches a ValueSet code directly, use it
  const directMatch = GENDER_IDENTITY_OPTIONS.find((o) => o.value === rawCode);
  if (directMatch) {
    return directMatch.value;
  }
  // If it's a legacy Patient.gender value (female/male/other/unknown), map to the ValueSet code
  const genderMatch = GENDER_IDENTITY_OPTIONS.find((o) => o.fhirGender === rawCode);
  return genderMatch?.value ?? rawCode;
}

function buildFormData(patient: Patient): PersonalInfoFormData {
  const address = patient.address?.[0];
  return {
    firstName: patient.name?.[0]?.given?.[0] ?? '',
    lastName: patient.name?.[0]?.family ?? '',
    email: getTelecomValue(patient.telecom, 'email'),
    phone: getTelecomValue(patient.telecom, 'phone'),
    birthDate: patient.birthDate ?? '',
    birthSex: getBirthSex(patient),
    gender: resolveGenderValue(getGenderIdentity(patient)),
    pronouns: getPronouns(patient),
    streetAddress: address?.line?.[0] ?? '',
    unit: address?.line?.[1] ?? '',
    city: address?.city ?? '',
    state: normalizeUsStateCode(address?.state) ?? address?.state ?? '',
    zipcode: stripSurroundingQuotes(address?.postalCode ?? ''),
    timezone: getPatientTimezone(patient),
  };
}

interface PersonalInfoSectionProps {
  patient: Patient;
  onPatientUpdated: (patient: Patient) => void;
}

export function PersonalInfoSection({ patient, onPatientUpdated }: PersonalInfoSectionProps): JSX.Element {
  const medplum = useMedplum();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<PersonalInfoFormData>(() => buildFormData(patient));

  const handleEdit = useCallback(() => {
    setFormData(buildFormData(patient));
    setEditing(true);
  }, [patient]);

  const handleCancel = useCallback(() => {
    setFormData(buildFormData(patient));
    setEditing(false);
  }, [patient]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await Sentry.startSpan({ op: 'http.client', name: 'Save Personal Info' }, async () => {
        const addressLines = [formData.streetAddress, formData.unit].filter(Boolean);
        let updated: Patient = {
          ...patient,
          name: [
            {
              ...(patient.name?.[0] ?? {}),
              given: [formData.firstName],
              family: formData.lastName,
            },
          ],
          telecom: setTelecomValue(setTelecomValue(patient.telecom, 'email', formData.email), 'phone', formData.phone),
          birthDate: formData.birthDate || undefined,
          address: [
            {
              ...(patient.address?.[0] ?? {}),
              line: addressLines.length > 0 ? addressLines : undefined,
              city: formData.city || undefined,
              state: formData.state || undefined,
              postalCode: formData.zipcode || undefined,
            },
          ],
        };

        const birthSexOption = SEX_AT_BIRTH_OPTIONS.find((o) => o.value === formData.birthSex);
        updated = setBirthSex(updated, formData.birthSex, birthSexOption?.system ?? '', birthSexOption?.label ?? '');

        const genderOption = GENDER_IDENTITY_OPTIONS.find((o) => o.value === formData.gender);
        updated = setGenderIdentity(
          updated,
          genderOption?.fhirGender ?? '',
          genderOption?.value ?? '',
          genderOption?.display ?? '',
          genderOption?.system ?? ''
        );

        updated = setPronouns(
          updated,
          formData.pronouns.map((code) => {
            const opt = PRONOUNS_OPTIONS.find((o) => o.value === code);
            return { code, display: opt?.label ?? code };
          })
        );

        updated = setPatientTimezone(updated, formData.timezone);

        const saved = await medplum.updateResource(updated);
        onPatientUpdated(saved);
      });

      setEditing(false);
      showNotification({ color: 'green', message: 'Personal information updated.' });
    } catch (error) {
      Sentry.captureException(error);
      showNotification({ color: 'red', message: friendlyErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }, [medplum, patient, formData, onPatientUpdated]);

  const updateField = useCallback((field: keyof PersonalInfoFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const pronounLabel = formData.pronouns
    .map((code) => PRONOUNS_OPTIONS.find((o) => o.value === code)?.label)
    .filter(Boolean)
    .join(' + ');
  const birthSexLabel = SEX_AT_BIRTH_OPTIONS.find((o) => o.value === formData.birthSex)?.label ?? '';
  const genderLabel = GENDER_IDENTITY_OPTIONS.find((o) => o.value === formData.gender)?.label ?? '';
  const timezoneLabel = US_TIMEZONES.find((o) => o.value === formData.timezone)?.label ?? formData.timezone;

  return (
    <SettingsSection
      title="Personal Information"
      editing={editing}
      onEdit={handleEdit}
      onSave={handleSave}
      onCancel={handleCancel}
      saving={saving}
    >
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
        {editing ? (
          <>
            <TextInput
              label="First name"
              value={formData.firstName}
              onChange={(e) => updateField('firstName', e.currentTarget.value)}
            />
            <TextInput
              label="Last name"
              value={formData.lastName}
              onChange={(e) => updateField('lastName', e.currentTarget.value)}
            />
            <TextInput
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => updateField('email', e.currentTarget.value)}
            />
            <TextInput
              label="Phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => updateField('phone', e.currentTarget.value)}
            />
            <TextInput
              label="Date of birth"
              type="date"
              value={formData.birthDate}
              onChange={(e) => updateField('birthDate', e.currentTarget.value)}
            />
            <Select
              label="Sex at birth"
              data={SEX_AT_BIRTH_OPTIONS}
              value={formData.birthSex}
              onChange={(v) => updateField('birthSex', v ?? '')}
              clearable
            />
            <Select
              label="Gender identity"
              data={GENDER_IDENTITY_OPTIONS}
              value={formData.gender}
              onChange={(v) => updateField('gender', v ?? '')}
              clearable
            />
            <MultiSelect
              label="Preferred pronouns"
              data={PRONOUNS_OPTIONS}
              value={formData.pronouns}
              onChange={(v) => setFormData((prev) => ({ ...prev, pronouns: v }))}
              clearable
            />
            <TextInput
              label="Street address"
              value={formData.streetAddress}
              onChange={(e) => updateField('streetAddress', e.currentTarget.value)}
            />
            <TextInput
              label="Unit"
              value={formData.unit}
              onChange={(e) => updateField('unit', e.currentTarget.value)}
            />
            <TextInput
              label="City"
              value={formData.city}
              onChange={(e) => updateField('city', e.currentTarget.value)}
            />
            <Select
              label="State"
              data={[...US_STATE_SELECT_OPTIONS]}
              value={formData.state}
              onChange={(v) => updateField('state', v ?? '')}
              searchable
              clearable
            />
            <TextInput
              label="Zipcode"
              value={formData.zipcode}
              onChange={(e) => updateField('zipcode', e.currentTarget.value)}
            />
            <Select
              label="Time Zone"
              data={US_TIMEZONES}
              value={formData.timezone}
              onChange={(v) => updateField('timezone', v ?? 'UTC')}
            />
          </>
        ) : (
          <>
            <FieldDisplay label="First name" value={formData.firstName} />
            <FieldDisplay label="Last name" value={formData.lastName} />
            <FieldDisplay label="Email" value={formData.email} />
            <FieldDisplay label="Phone" value={formData.phone} />
            <FieldDisplay label="Date of birth" value={formatDate(formData.birthDate)} />
            <FieldDisplay label="Sex at birth" value={birthSexLabel} />
            <FieldDisplay label="Gender identity" value={genderLabel} />
            <FieldDisplay label="Preferred pronouns" value={pronounLabel} />
            <FieldDisplay label="Street address" value={formData.streetAddress} />
            <FieldDisplay label="Unit" value={formData.unit} />
            <FieldDisplay label="City" value={formData.city} />
            <FieldDisplay
              label="State"
              value={US_STATE_SELECT_OPTIONS.find((option) => option.value === formData.state)?.label ?? formData.state}
            />
            <FieldDisplay label="Zipcode" value={formData.zipcode} />
            <FieldDisplay label="Time Zone" value={timezoneLabel} />
          </>
        )}
      </SimpleGrid>
    </SettingsSection>
  );
}

function FieldDisplay({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm">{value || '—'}</Text>
    </div>
  );
}
